import { useState } from 'react';

import { MangopayHostedIdvIframeDialog } from './MangopayHostedIdvIframeDialog/index.tsx';
import type { HostedVerificationFlow } from './returnUrl.ts';
import { useHostedIdvIframeMode } from './useHostedIdvIframeMode.ts';
import { useStartFiatWalletIdentityVerification } from './useStartFiatWalletIdentityVerification.ts';

type Params = {
  flow?: HostedVerificationFlow;
  beforeLaunch?: () => Promise<boolean>;
  onVerificationSubmitted: () => void;
};

export const useLaunchHostedIdentityVerification = ({
  flow,
  beforeLaunch,
  onVerificationSubmitted,
}: Params) => {
  const iframeMode = useHostedIdvIframeMode();
  const { startIdentityVerification, loading: starting } =
    useStartFiatWalletIdentityVerification({ flow, framed: iframeMode });
  const [hostedUrl, setHostedUrl] = useState<string | null>(null);
  const [completed, setCompleted] = useState(false);
  const [preparing, setPreparing] = useState(false);

  const launch = async () => {
    if (beforeLaunch) {
      setPreparing(true);
      const proceed = await beforeLaunch()
        .catch(() => false)
        .finally(() => setPreparing(false));
      if (!proceed) return;
    }

    const url = await startIdentityVerification();
    if (!url) return;

    if (iframeMode) {
      setHostedUrl(url);
    } else {
      window.open(url, '_self');
    }
  };

  const onCompleted = () => {
    setCompleted(true);
    setHostedUrl(null);
  };

  const onClosed = () => {
    if (completed) {
      onVerificationSubmitted();
    }
  };

  const dialog = (
    <MangopayHostedIdvIframeDialog
      open={!!hostedUrl}
      url={hostedUrl}
      onCompleted={onCompleted}
      onClose={() => setHostedUrl(null)}
      onClosed={onClosed}
    />
  );

  const loading = preparing || starting;

  return { launch, loading, iframeMode, dialog };
};
